import { useEffect, useState } from "react";
import Footer from "./layouts/Footer";
import Header from "./layouts/Header";
import PageContent from "./layouts/PageContent";
import Sidebar from "./layouts/Sidebar";
import { Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Cookies from "universal-cookie";
import { Monitor } from "react-feather";
import WelcomeOverlay from "./components/common/WelcomeOverlay";
import { setUserInfo, setUserToken } from "./redux/authSlice";
import LocationGate from "./components/common/LocationGate";
import AnnouncementGate from "./components/common/AnnouncementGate";
import EmergencyContactGate from "./components/common/EmergencyContactGate";
import TechBackdrop from "./components/common/TechBackdrop";
import xavaLogo from "./assets/images/xava-logo.png";

const cookies = new Cookies();
const MOBILE_QUERY = "(max-width: 767px)";

function App({ children }) {
  const userToken = useSelector((state) => state?.authState.userToken)
  const userInfo = useSelector((state) => state?.authState.userInfo)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if(!userToken){
      navigate('/')
    }
  },[userToken])

  // Onboarding gate: employees must have submitted their details AND uploaded
  // every required document (photo, Aadhaar front/back, PAN). Anyone missing
  // any of it is routed to the onboarding form until complete.
  useEffect(() => {
    if (userInfo?.role !== "employee") return;
    const docsMissing =
      !userInfo?.profilepic ||
      !userInfo?.aadharcardfront ||
      !userInfo?.aadharcardback ||
      !userInfo?.pancard;
    const needsOnboarding = userInfo?.hasSubmittedDetails === false || docsMissing;
    if (needsOnboarding && location.pathname !== "/employee-form") {
      navigate("/employee-form");
    }
  }, [userInfo, location.pathname]);

  // Same first-login gate for contractors (their own, lighter wizard)
  useEffect(() => {
    if (
      userInfo?.role === "contractor" &&
      userInfo?.hasSubmittedDetails === false &&
      location.pathname !== "/contractor-onboarding"
    ) {
      navigate("/contractor-onboarding");
    }
  }, [userInfo, location.pathname]);

  // Desktop-only for employees, HR (staff) and contractors — attendance and
  // approvals are deliberate desktop workflows. Admin / super admin keep the
  // responsive experience on any device.
  const mobileBlocked =
    isMobile && ["employee", "staff", "contractor"].includes(userInfo?.role);

  if (mobileBlocked) {
    const handleLogout = () => {
      localStorage.clear();
      cookies.remove("MMCL");
      cookies.remove("MMCL_user");
      cookies.remove("isLoggedIn");
      dispatch(setUserInfo({}));
      dispatch(setUserToken(""));
      navigate("/");
    };
    return (
      <div className="xv-mobile-block">
        <img src={xavaLogo} alt="Xava Web Services" />
        <span className="xv-mobile-block-icon">
          <Monitor size={26} />
        </span>
        <h1>Please switch to a laptop</h1>
        <p>
          Xava CRM isn&apos;t available on mobile for your account. Open{" "}
          <strong>crm.xavawebservices.com</strong> on your laptop or desktop to
          check in, raise requests and manage your work.
        </p>
        <button type="button" onClick={handleLogout}>
          Log out
        </button>
      </div>
    );
  }

  const shell = (
    <div className="wrapper vertical-layout navbar-floating footer-static vertical-menu-modern menu-expanded">
      <TechBackdrop />
      <WelcomeOverlay />
      {userInfo?.role === "employee" &&
        userInfo?.hasSubmittedDetails !== false &&
        location.pathname !== "/employee-form" && <LocationGate />}
      {location.pathname !== "/employee-form" &&
        location.pathname !== "/contractor-onboarding" && <AnnouncementGate />}
      <Sidebar />
      <PageContent children={children} />
      {/* <Header /> */}
      <div className="sidenav-overlay"></div>
      <Footer />
      <Toaster />
    </div>
  );

  // Employees stay locked out until HR has called and verified their
  // emergency contact. Onboarding (the form itself) stays reachable so a new
  // joiner can enter the number in the first place.
  if (
    userInfo?.role === "employee" &&
    userInfo?.hasSubmittedDetails !== false &&
    location.pathname !== "/employee-form"
  ) {
    return <EmergencyContactGate>{shell}</EmergencyContactGate>;
  }

  return shell;
}

export default App;
